import {
  notEmpty,
  parseBoolean,
  parseJsonArray,
  parseRecord,
  parseString,
  parseStringUnion,
} from "@de-xima/xima-common-js-lang";
import jQuery from "jquery";

import { InsertHints, type IDialogDataTableMaskPanelOptions, type IInsertData, type IMaskPanel } from "../../Header.ts";
import { getWidgetByVar, runRemoteCommand } from "../../Util.ts";
import { AbstractMaskPanel } from "../AbstractMaskPanel.ts";

const ATTR_DATA_ROW_KEY = "data-rk";
const DATA_PLACEHOLDER_VALUE = "placeholderValue";
const DATA_INSERT_HINT = "insertHint";
const DEFAULT_DATA_TABLE_MASK_HANDLER_OPTIONS = {
  dialogWidgetVar: "",
  tableWidgetVar: "",
};
const SELECTOR_PLACEHOLDER_VALUE = ".ui-placeholder-value";

/**
 * The handler for the current implementation of the placeholder mask as
 * a `<p:dialog>` with a `<p:datatable>`.
 */
export class DialogDataTableMaskPanel extends AbstractMaskPanel implements IMaskPanel {
  private static _defaultInstance: DialogDataTableMaskPanel | undefined;

  /**
   * The options for this instance.
   */
  private _initialized: boolean;

  /**
   * The options which with this instance was configured.
   */
  private _options: IDialogDataTableMaskPanelOptions;

  /**
   * @param options Options to configure this instance.
   */
  constructor(options: Partial<IDialogDataTableMaskPanelOptions>) {
    super();
    this._options = Object.assign({}, DEFAULT_DATA_TABLE_MASK_HANDLER_OPTIONS, options);
    if (this._options.dialogWidgetVar === undefined) {
      throw new Error("No dialogWidgetVar given");
    }
    if (this._options.tableWidgetVar === undefined) {
      throw new Error("No tableWidgetVar given");
    }
    this._initialized = false;
  }

  override close(): void {
    const dialog = this._getDialogWidget();
    dialog?.hide();
  }

  override async getSelectedValue(): Promise<IInsertData | undefined> {
    return this._getSelectedValue();
  }

  insert(close?: boolean): void {
    const placeholder = this._getSelectedValue();
    if (notEmpty(placeholder?.content)) {
      this._insertionListener({ close, placeholder });
    }
  }

  override async isOpen(): Promise<boolean> {
    const dialog = this._getDialogWidget();
    return dialog !== undefined ? dialog.isVisible() : false;
  }

  override onExternalChange(): void {
    this._moveToTop();
    this._getSearchField().trigger("keyup");
    this._restoreCollapse();
  }

  override open($position: JQuery): void {
    const dialog = this._getDialogWidget();
    if (dialog !== undefined) {
      dialog.show();
      const isVisible = dialog.isVisible();
      if (isVisible) {
        this._onOpen($position, isVisible);
      } else {
        dialog.jq.one("open", this._onOpen.bind(this, $position, isVisible));
      }
    }
  }

  override update(): void {
    runRemoteCommand(BackendCommand?.updatePlaceholderContent).catch((e: unknown) => {
      // Abort usually happens when the user reloads the page, which aborts requests
      if (parseRecord(e).error !== "abort") {
        console.error("Could not update placeholder content", e);
      }
    });
  }

  /**
   * @returns The currently selected row.
   */
  private _findSelectedRowElement(): JQuery {
    const dt = this._getTableWidget();
    const selectedRowKey = dt !== undefined ? dt.selection[0] : undefined;
    if (selectedRowKey === undefined) {
      return jQuery();
    }
    const rows = this._getRows();
    for (const row of rows) {
      if (row.getAttribute(ATTR_DATA_ROW_KEY) === selectedRowKey) {
        return jQuery(row);
      }
    }
    return jQuery();
  }

  /**
   * Puts focus on the table with the placeholders, so that the user can
   * select a placeholder via the keyboard (arrow keys).
   */
  private _focusTable(): void {
    const tableWidget = this._getTableWidget();
    let $row = this._findSelectedRowElement();
    if ($row.length === 0) {
      $row = this._getRows().first();
    }
    // Are there any placeholders at all?
    if ($row.length > 0 && tableWidget !== undefined) {
      tableWidget.unselectAllRows();
      tableWidget.selectRow($row);
      tableWidget.getTbody().trigger("focus");
    }
  }

  /**
   * @returns The dialog widget for the placeholder dialog.
   */
  private _getDialogWidget(): PrimeFaces.widget.Dialog | undefined {
    const dialogWidget = getWidgetByVar(this._getDialogWidgetVar(), PrimeFaces.widget.Dialog);
    if (dialogWidget === undefined) {
      console.warn("DialogDataTableMaskPanel not found for widget var:", this._getDialogWidgetVar());
      return undefined;
    }
    this._initializeDialogWidget(dialogWidget);
    return dialogWidget;
  }

  /**
   * @returns The widget variable for the placeholder dialog.
   */
  private _getDialogWidgetVar(): string {
    return this._options.dialogWidgetVar ?? "";
  }

  /**
   * @returns List of groups that are expanded.
   */
  private _getGroupCollapse(): Set<string> {
    const dialog = this._getDialogWidget();
    const value = dialog !== undefined ? (dialog.jq.find(".ui-placeholder-collapse").val() ?? "[]") : "[]";
    const parsed = parseJsonArray(value);
    return new Set(parsed.map(String));
  }

  /**
   * @param $row A (header) row of the datatable.
   * @returns Group ID of the row.
   */
  private _getGroupId($row: JQuery): string {
    return parseString($row.find(".ui-placeholder-group-wrapper").data("groupId"));
  }

  private _getRows() {
    const widget = this._getTableWidget();
    return widget !== undefined ? widget.getTbody().find(widget.rowSelector) : jQuery();
  }

  /**
   * @returns The search field for filtering the placeholder mask.
   */
  private _getSearchField(): JQuery {
    const dialog = this._getDialogWidget();
    if (dialog === undefined) {
      return jQuery();
    }
    return dialog.jq.find(".searchbar input");
  }

  private _getSelectedValue(): IInsertData | undefined {
    const $row = this._findSelectedRowElement();
    if ($row.length === 0) {
      return undefined;
    }
    const $val = $row.find(SELECTOR_PLACEHOLDER_VALUE);
    const content = parseString($val.data(DATA_PLACEHOLDER_VALUE));
    const insertHint = parseStringUnion($val.data(DATA_INSERT_HINT), InsertHints);
    return { content, insertHint };
  }

  /**
   * @returns The datable widget for the placeholder table.
   */
  private _getTableWidget(): PrimeFaces.widget.DataTable | undefined {
    return getWidgetByVar(this._getTableWidgetVar(), PrimeFaces.widget.DataTable);
  }

  /**
   * @returns The widget variable for the placeholder table.
   */
  private _getTableWidgetVar(): string {
    return this._options.tableWidgetVar ?? "";
  }

  /**
   * Initializes this placeholder mask, if not initialized already.
   * @param dialogWidget Dialog widget for this placeolder dialog.
   */
  private _initializeDialogWidget(dialogWidget: PrimeFaces.widget.Dialog): void {
    if (this._initialized) {
      return;
    }
    // When using the keyboard, insert the placeholder when Enter is pressed.
    dialogWidget.jq.on("keyup", "tbody.ui-widget-content", (event) => {
      if (event.key === "Enter") {
        // Enter pressed on placeholder.
        this.insert();
      }
    });

    // Allow expand / collapse even when clicking on the text of the placeholder group
    dialogWidget.jq.on("click", ".ui-placeholder-group-wrapper", (e) => {
      jQuery(e.target).closest("td").find(".ui-rowgroup-toggler").trigger("click");
    });

    // Save which groups are expanded and which are collapsed during an update
    dialogWidget.jq.on("click", ".ui-rowgroup-toggler", (e) => {
      const groupCollapse = this._getGroupCollapse();
      const groupId = parseString(
        jQuery(e.target).closest(".ui-placeholder-group").find(".ui-placeholder-group-wrapper").data("groupId"),
      );
      const isExpanded = parseBoolean(
        jQuery(e.target).closest(".ui-placeholder-group").find(".ui-rowgroup-toggler").attr("aria-expanded"),
      );
      if (isExpanded) {
        groupCollapse.delete(groupId);
      } else {
        groupCollapse.add(groupId);
      }
      this._setGroupCollapse(groupCollapse);
    });

    // Make dialog resizable. Add a class to the body during
    // resize, which blocks pointer event on iframes. This is
    // needed to to prevent the mouse from getting stuck, when
    // the user moves over an iframe while resizing the dialog.
    dialogWidget.jq.resizable({
      start: () => document.body.classList.add("ui-resizing"),
      stop: () => document.body.classList.remove("ui-resizing"),
    });

    this._initialized = true;
  }

  /**
   * @param $row A (header) row of the datatable.
   * @returns `true` iff the header row is the header row for form placeholders.
   */
  private _isExpandInitially($row: JQuery): boolean {
    return parseBoolean($row.find(".ui-placeholder-group-wrapper").data("expandInitially"));
  }

  /**
   * Moves the placeholder dialog to the top, iff it is visible. If it is not visible, do nothing.
   */
  private _moveToTop(): void {
    const dialog = this._getDialogWidget();
    if (dialog?.isVisible()) {
      dialog.moveToTop();
    }
  }

  /**
   * Callback when the dialog was loaded and opened. Moves it to the top, puts
   * focus on the table, and positions it correctly.
   * @param $position Input component relative to which the dialog is positioned.
   * @param wasVisible Whether the dialog was already visible before.
   */
  private _onOpen($position: JQuery | undefined, wasVisible: boolean): void {
    // The open behavior is fired before the datatable is rendered synchronously (invokeDeferredRenderers).
    // Thus we must delay until at least the next event loop.
    setTimeout(() => {
      this._setInitialExpandedState(wasVisible);
      // Make sure we are on top.
      this._moveToTop();
      // Focus on the table for keyboard input.
      this._focusTable();
      // Position dialog relatively to the input component.
      if ($position !== undefined) {
        this._position($position);
      }
    }, 0);
  }

  /**
   * Positions the placeholder dialog relative to the given input component.
   * @param $position Input component relative to which to position
   * the placeholder dialog.
   */
  private _position($position: JQuery): void {
    const dialog = this._getDialogWidget();
    if (dialog !== undefined) {
      if ($position.length > 0) {
        dialog.jq.position({
          at: "left top",
          collision: "fit",
          my: "right top",
          of: $position,
        });
      }
    }
  }

  private _restoreCollapse() {
    //  Iterate over all header rows; and collapse those that should be collapsed.
    const groupCollapse = this._getGroupCollapse();
    const dialog = this._getDialogWidget();
    if (dialog !== undefined) {
      for (const row of dialog.jq.find(".ui-rowgroup-header")) {
        const $row = jQuery(row);
        const rows = $row.nextUntil(".ui-rowgroup-header");

        // Collapse if (a) this category contains no placeholders or (b) the placeholder is not a form placeholder
        // and there are form placeholders available
        const groupId = this._getGroupId($row);
        const collapse = groupCollapse.has(groupId);

        if (collapse) {
          rows.hide();
          $row
            .find(".ui-rowgroup-toggler-icon")
            .addClass("ui-icon-circle-triangle-e")
            .removeClass("ui-icon-circle-triangle-s");
        }
      }
    }
  }

  /**
   * @param groupCollapse List of groups that are expanded.
   */
  private _setGroupCollapse(groupCollapse: Set<string> | undefined): void {
    const value = groupCollapse !== undefined ? JSON.stringify(Array.from(groupCollapse)) : [];
    const dialog = this._getDialogWidget();
    if (dialog !== undefined) {
      dialog.jq.find(".ui-placeholder-collapse").val(value);
    }
  }

  /**
   * Collapse all placeholder types for which no placeholders are available. Also collapse
   * all placeholders types except form placeholders, unless there are not form placeholders.
   * @param wasVisible Whether the dialog was already visible before.
   */
  private _setInitialExpandedState(wasVisible: boolean): void {
    if (wasVisible) {
      return;
    }

    const groupCollapse = this._getGroupCollapse();
    const dialog = this._getDialogWidget();

    //  Iterate over all header rows; and collapse those that should be collapsed.
    if (dialog !== undefined) {
      for (const row of dialog.jq.find(".ui-rowgroup-header")) {
        const $row = jQuery(row);
        const rows = $row.nextUntil(".ui-rowgroup-header");
        const groupId = this._getGroupId($row);

        // Collapse if (a) this category contains no placeholders or (b) the placeholder is not a form placeholder
        // and there are form placeholders available
        const expandInitially = this._isExpandInitially($row);
        const collapse = !expandInitially;

        if (collapse) {
          rows.hide();
          $row
            .find(".ui-rowgroup-toggler-icon")
            .addClass("ui-icon-circle-triangle-e")
            .removeClass("ui-icon-circle-triangle-s");
          groupCollapse.add(groupId);
        } else {
          groupCollapse.delete(groupId);
        }
      }
    }

    this._setGroupCollapse(groupCollapse);
  }

  static default(): DialogDataTableMaskPanel {
    if (DialogDataTableMaskPanel._defaultInstance === undefined) {
      DialogDataTableMaskPanel._defaultInstance = new DialogDataTableMaskPanel({
        dialogWidgetVar: "placeholderMask",
        tableWidgetVar: "placeholderTable",
      });
    }
    return DialogDataTableMaskPanel._defaultInstance;
  }
}
